import { Box } from '@mui/material'
import { morphForWords } from 'components/shared/processes'
import moment from 'moment'
import ReactApexChart from 'react-apexcharts'
import { COLORS } from 'styles/colors'
import ChartLegend from 'ui/charts/legends/ChartLegend'

import { localizationApexCharts } from 'utils'

interface FatnessChartProps {
	normalCategories: number[]
	deviationCategories: number[]
	obesityCategories: number[]
	emaciationCategories: number[]
	timestamp: Array<number | string>
	shouldUseCategoryAxis: boolean
	startDate: number
	endDate: number
}

const FatnessChart = (props: FatnessChartProps) => {
	const {
		normalCategories,
		deviationCategories,
		obesityCategories,
		emaciationCategories,
		timestamp,
		shouldUseCategoryAxis,
		startDate,
		endDate,
	} = props
	const categoryValues = [
		...normalCategories,
		...deviationCategories,
		...obesityCategories,
		...emaciationCategories,
	]
	const hasData = categoryValues.some(value => value > 0)
	const startTimestamp = moment(startDate).valueOf()
	const selectedEndTimestamp = moment(endDate).valueOf()
	const endTimestamp =
		selectedEndTimestamp > startTimestamp
			? selectedEndTimestamp
			: moment(startTimestamp).endOf('day').valueOf()
	const hasValidTimestampRange =
		Number.isFinite(startTimestamp) &&
		Number.isFinite(endTimestamp) &&
		endTimestamp > startTimestamp
	const noDataTimestamps = hasValidTimestampRange
		? Array.from(
				{ length: 121 },
				(_, index) =>
					startTimestamp + ((endTimestamp - startTimestamp) * index) / 120,
			)
		: timestamp
	const noDataCategories = noDataTimestamps.map((_, index) => {
		const denominator = Math.max(noDataTimestamps.length - 1, 1)
		const progress = index / denominator

		return 0.5 - 0.25 * Math.sin(progress * Math.PI * 3)
	})
	const chartTimestamps = hasData ? timestamp : noDataTimestamps
	const series: any = hasData
		? [
				{
					name: 'В норме',
					data: normalCategories,
				},
				{
					name: 'С отклонением',
					data: deviationCategories,
				},
				{
					name: 'С ожирением',
					data: obesityCategories,
				},
				{
					name: 'С истощением',
					data: emaciationCategories,
				},
			]
		: [
				{
					name: 'Нет данных',
					data: noDataCategories,
				},
			]

	const options: any = {
		mask: false,
		colors: hasData
			? [COLORS.main, COLORS.health, COLORS.error, COLORS.black]
			: [COLORS.inputBorder],
		chart: {
			locales: localizationApexCharts(),
			defaultLocale: 'ru',
			zoom: {
				enabled: false,
			},
			height: 250,
			type: 'line',
			toolbar: {
				show: false,
			},
		},
		markers: {
			size: 0,
			colors: ['#fff'],
			strokeColors: hasData
				? [COLORS.main, COLORS.health, COLORS.error, COLORS.black]
				: [COLORS.inputBorder],
			strokeWidth: 2,
			hover: {
				size: 5,
			},
		},
		stroke: {
			width: hasData ? 2 : 1.5,
			curve: 'straight',
			lineCap: hasData ? 'butt' : 'round',
		},
		title: {
			style: {
				fontSize: '12px',
				fontFamily: 'Montserrat',
				fontStyle: 'normal',
				fontWeight: 400,
				lineHeight: '15px',
				color: '#7F7F84',
			},
			text: 'Количество голов',
		},

		dataLabels: {
			enabled: false,
		},
		grid: {
			strokeDashArray: [6, 3],
			show: true,
			xaxis: {
				lines: {
					show: true,
				},
			},
		},
		xaxis: {
			crosshairs: {
				show: true,
				opacity: 1,
				position: 'back',
				stroke: {
					width: 1,
					dashArray: 0,
				},
			},
			type: hasData && shouldUseCategoryAxis ? 'category' : 'datetime',
			tooltip: {
				enabled: false,
			},
			labels: {
				datetimeUTC: false,
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
				datetimeFormatter: {
					year: 'yyyy',
					month: 'MM',
					day: 'dd.MM',
					hour: 'HH:mm',
				},
				formatter: (val: number) => moment(val).format('DD.MM'),
			},
			categories: chartTimestamps,
		},
		yaxis: {
			tickAmount: 4,
			min: 0,
			max: hasData ? Math.round(Math.max(0, ...categoryValues)) : 1,
			labels: {
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
				formatter: function (value: number, index: number) {
					if (!hasData) {
						return value === 0 ? '0' : '-'
					}
					if (index % 2 !== 0) {
						return ''
					}
					return Math.round(value).toString()
				},
			},
		},
		tooltip: {
			fixed: false,
			custom: ({ dataPointIndex }) => {
				const timeValue = moment(chartTimestamps[dataPointIndex]).format(
					'D.MM.YY',
				)
				if (!hasData) {
					return `<div style='width:max-content; height:auto; background-color:#fff; border-radius:12px; border:hidden; box-shadow:0px 0px 16px 0px rgba(4,6,6,0.16)'>
						<div style='padding:16px 8px; display:flex; flex-direction:column; gap:4px'>
							<p style='font-weight:400; font-size:12px; color:#7F7F84; margin:0'>Нет данных</p>
							<p style='font-weight:400; font-size:12px; color:#7F7F84; margin:0'>${timeValue}</p>
						</div>
					</div>`
				}
				const points = [
					{ color: COLORS.main, value: normalCategories[dataPointIndex] ?? 0 },
					{
						color: COLORS.health,
						value: deviationCategories[dataPointIndex] ?? 0,
					},
					{
						color: COLORS.error,
						value: obesityCategories[dataPointIndex] ?? 0,
					},
					{
						color: COLORS.black,
						value: emaciationCategories[dataPointIndex] ?? 0,
					},
				]
				const pointsHtml = points
					.map(
						point => `
							<div style='display:flex; flex-direction:row; align-items:center; gap:4px'>
								<span style='width:12px; height:12px; border-radius:50%; background-color:${point.color}'></span>
								<p style='font-weight:600; font-size:16px; color:#000; margin:0'>${point.value}</p>
								<p style='font-weight:400; font-size:12px; color:#7F7F84; margin:0'>${morphForWords(
									point.value,
									['голова', 'головы', 'голов'],
								)}</p>
							</div>`,
					)
					.join('')

				return `<div
				style='width:max-content; height:auto; background-color: #fff; border-radius:12px; border: hidden; box-shadow: 0px 0px 16px 0px rgba(4, 6, 6, 0.16); position: relative'>
				<div style='padding:16px 8px; border: none;'>
					<div style='display: flex; flex-direction: column; gap:4px'>
						${pointsHtml}
						<div>
							<p style='font-weight: 400; font-size: 12px; color: #7F7F84; margin: 0px'>${timeValue}</p>
						</div>
					</div>
				</div>
			</div>`
			},
		},
		legend: {
			show: false,
		},
	}
	const descriptionForLegend = [
		{ color: COLORS.main, description: 'В норме' },
		{ color: COLORS.health, description: 'С отклонением' },
		{ color: COLORS.error, description: 'С ожирением' },
		{ color: COLORS.black, description: 'С истощением' },
		{ color: COLORS.inputBorder, description: 'Нет данных' },
	]
	return (
		<Box
			className={hasData ? 'fatness-chart-has-data' : 'fatness-chart-no-data'}
			sx={{ minHeight: '250px', display: 'flex', flexDirection: 'column' }}
		>
			<ReactApexChart
				id={'fatnessChart'}
				options={options}
				series={series}
				type='line'
				height={250}
			/>
			<ChartLegend initialLegends={descriptionForLegend} />
		</Box>
	)
}

export default FatnessChart
